import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Quote, ArrowRight } from "lucide-react";
import { getCaseStudy } from "@/lib/api";
import { CASE } from "@/constants/testIds";
import Seo, { caseStudyJsonLd, breadcrumbJsonLd } from "@/components/Seo";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setC(null);
    setErr(false);
    getCaseStudy(slug).then(setC).catch(() => setErr(true));
  }, [slug]);

  if (err) {
    return (
      <section className="py-24 bg-lovac">
        <Seo
          title="Caso studio non trovato | Lovac"
          description="Il caso studio richiesto non esiste."
          path={`/casi-studio/${slug}`}
          noindex
        />
        <div className="container-narrow text-center">
          <h1 className="font-display text-3xl text-lovac-dark">Caso studio non trovato</h1>
          <Link to="/casi-studio" className="link-underline mt-6 inline-block">← Tutti i casi studio</Link>
        </div>
      </section>
    );
  }

  if (!c) {
    return (
      <section className="py-24 bg-lovac">
        <div className="container-lovac">
          <div className="card-lovac h-80 animate-pulse opacity-40" />
        </div>
      </section>
    );
  }

  // Determina il numero di colonne ideali per i dati tecnici
  const nData = c.dati_tecnici?.length || 0;
  const dataColsClass =
    nData >= 4 ? "sm:grid-cols-2 lg:grid-cols-4" :
    nData === 3 ? "sm:grid-cols-3" :
    nData === 2 ? "sm:grid-cols-2" :
    "sm:grid-cols-1";

  return (
    <>
      <Seo
        title={`Come ${c.nome_azienda} ha trovato un ${c.ruolo_cercato} in ${c.time_to_hire} | Lovac`}
        description={`Scopri come Lovac ha supportato ${c.nome_azienda} (${c.settore}) nella ricerca e selezione di un ${c.ruolo_cercato} in ${c.time_to_hire}.`}
        path={`/casi-studio/${c.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            caseStudyJsonLd(c),
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Casi studio", path: "/casi-studio" },
              { name: c.nome_azienda, path: `/casi-studio/${c.slug}` },
            ]),
          ],
        }}
      />
      {/* HERO, più compatto */}
      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-lovac px-6 pt-16 pb-16 relative z-10">
          <Link to="/casi-studio" data-testid={CASE.detailBack} className="inline-flex items-center gap-2 text-sm text-on-dark-muted hover:text-on-dark mb-8">
            <ArrowLeft size={14} /> Tutti i casi studio
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="h-14 w-14 rounded-xl bg-[rgba(255,255,255,0.05)] border border-lovac-dark flex items-center justify-center text-lg font-medium text-on-dark">
              {c.logo_iniziale}
            </div>
            <div>
              <p className="text-on-dark text-lg font-display leading-tight">{c.nome_azienda}</p>
              <p className="text-on-dark-muted text-sm">{c.settore}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="badge-on-dark">{c.tipo_figura}</span>
            <span className="text-xs text-on-dark-muted uppercase tracking-wider">
              Time to hire <span className="text-lovac-accent font-mono ml-1">{c.time_to_hire}</span>
            </span>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-on-dark max-w-4xl leading-[1.1]">
            {c.titolo_pagina || c.ruolo_cercato}
          </h1>
        </div>
      </section>

      {/* META, striscia compatta */}
      {c.meta_fields?.length > 0 && (
        <section className="bg-lovac border-b border-lovac">
          <div className="container-lovac px-6 py-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-5">
            {c.meta_fields.map((m) => (
              <div key={m.k}>
                <p className="text-[11px] uppercase tracking-wider text-lovac-muted">{m.k}</p>
                <p className="text-sm text-lovac-dark mt-1.5 font-medium">{m.v}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* BODY, narrow + spacing ridotto, niente two-column sticky */}
      <section className="py-20 bg-lovac">
        <div className="container-narrow space-y-14">
          <Block badge="Il problema" text={c.testo_problema} />
          <Block badge="La ricerca" text={c.testo_ricerca} />
        </div>
      </section>

      {/* RISULTATO, band scura compatta */}
      <section className="py-16 bg-lovac-dark text-on-dark">
        <div className="container-lovac px-6 grid lg:grid-cols-[auto_1fr] gap-10 items-center">
          <div className="lg:border-r lg:border-lovac-dark lg:pr-10">
            <p className="badge-on-dark">Il risultato</p>
            <p className="font-mono text-lovac-accent mt-4 text-xs uppercase tracking-wider">Time to hire</p>
            <p className="font-display text-5xl text-on-dark mt-1">{c.time_to_hire}</p>
          </div>
          <div className="text-lg text-on-dark leading-relaxed max-w-3xl">
            {c.testo_risultato}
          </div>
        </div>
      </section>

      {/* CITAZIONI, testo più grande, più aria */}
      {c.citazioni_aggiuntive?.length > 0 && (
        <section className="py-20 bg-lovac">
          <div className="container-lovac">
            <p className="badge-lovac">Cosa dice il cliente</p>
            <div className="mt-10 grid lg:grid-cols-3 gap-6">
              {c.citazioni_aggiuntive.map((q, i) => (
                <blockquote key={i} className="card-lovac flex flex-col h-full p-8">
                  <Quote size={22} className="text-lovac-accent mb-5" />
                  <p className="text-[17px] sm:text-lg text-lovac-dark leading-[1.65] font-display flex-1">
                    "{q.testo || q}"
                  </p>
                  <p className="text-xs text-lovac-muted mt-8 pt-5 border-t border-lovac">
                    {q.autore || `${c.nome_cliente}, ${c.ruolo_cliente}`}
                  </p>
                </blockquote>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DATI TECNICI, box affiancati invece di tabella */}
      {c.dati_tecnici?.length > 0 && (
        <section className="py-20 bg-lovac-2">
          <div className="container-lovac">
            <p className="badge-lovac">Dati tecnici</p>
            <h2 className="font-display text-3xl mt-3 text-lovac-dark">Numeri della ricerca</h2>
            <div className={`mt-10 grid grid-cols-1 ${dataColsClass} gap-4`}>
              {c.dati_tecnici.map((d) => (
                <div key={d.k} className="card-lovac h-full p-6 flex flex-col">
                  <p className="text-[11px] uppercase tracking-wider text-lovac-muted">{d.k}</p>
                  <p className="text-xl font-display text-lovac-dark mt-3 leading-snug">{d.v}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA, più compatto */}
      <section className="py-20 bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-narrow text-center relative z-10">
          <span className="badge-on-dark">Prenota una call</span>
          <h2 className="font-display text-3xl sm:text-4xl mt-5 text-on-dark leading-tight">
            {c.cta_titolo || "Hai una posizione aperta da troppo tempo?"}
          </h2>
          <p className="text-on-dark-muted mt-4">
            Parlane con un recruiter Lovac. Trenta minuti, senza obbligo.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/contatti" className="btn-primary">Prenota una call <ArrowRight size={16} /></Link>
            <Link to="/casi-studio" className="btn-outline-dark">Altri casi studio</Link>
          </div>
        </div>
      </section>
    </>
  );
}

const Block = ({ badge, text }) => (
  <div>
    <p className="badge-lovac">{badge}</p>
    <div className="text-lg text-lovac-dark leading-[1.75] mt-5 whitespace-pre-line">
      {text}
    </div>
  </div>
);
